"use client";

// The signed-in member's OWN picture in Root's chrome. One shared read of `GET /api/me/avatar` (same-origin, the session cookie, never cached) feeds
// every header variant, the account menu and the login popup, so the face in them can never disagree and the page asks once.
//
// Only the SIGNED-IN member's own saved picture is ever drawn, and only while the read belongs to that same identity: a different person signing in
// on this browser is "loading" again, never shown the previous person's face. Signed out, unknown, failed, or a picture the browser cannot load all
// show that member's INITIALS (never the static guest icon, which only a signed-out visitor sees). Re-read on focus / when the tab becomes visible, and on a timer while an uploaded photo is shown (its
// signed address is short-lived); a changed revision is a new address, so a replaced photo is never served from cache.
import { useEffect, useState, useSyncExternalStore } from "react";
import type { CSSProperties } from "react";

import { isSignedIn, useCartStore } from "@/lib/cart-store";
import type { MemberAvatar } from "@/lib/member-avatar";

const REFRESH_MS = 5 * 60 * 1000;

interface Snapshot {
  who: string | null;
  avatar: MemberAvatar | null;
}
let snapshot: Snapshot = { who: null, avatar: null };
const listeners = new Set<() => void>();
/**
 * How many mounted faces (header, account menu, popup) are showing each identity. A response only lands while someone still shows the identity it was
 * asked for, so a slow read for a previous person is dropped, yet one face unmounting never disables another face's refresh.
 */
const active = new Map<string, number>();
/** Register one face for `who`; the returned function releases exactly that registration. */
export function trackMemberFace(who: string): () => void {
  active.set(who, (active.get(who) ?? 0) + 1);
  let released = false;
  return () => {
    if (released) return;
    released = true;
    const n = (active.get(who) ?? 1) - 1;
    if (n <= 0) active.delete(who);
    else active.set(who, n);
  };
}
let inflight: string | null = null;
const emit = (next: Snapshot) => {
  snapshot = next;
  listeners.forEach((l) => l());
};

function parse(json: unknown): MemberAvatar | null {
  const a = typeof json === "object" && json !== null ? (json as { avatar?: unknown }).avatar : null;
  if (typeof a !== "object" || a === null) return null;
  const { src, revision } = a as { src?: unknown; revision?: unknown };
  return typeof src === "string" && typeof revision === "string" ? { src, revision } : null;
}

/** Re-read the picture for `who`. A failed read keeps what this same person already had; a late answer for a previous identity is ignored. */
export async function refreshMemberAvatar(who: string): Promise<void> {
  if (inflight === who) return;
  inflight = who;
  try {
    const res = await fetch("/api/me/avatar", { cache: "no-store", credentials: "same-origin", headers: { Accept: "application/json" } });
    const json: unknown = res.ok ? await (res.json() as Promise<unknown>).catch(() => null) : null;
    if (!active.has(who)) return;
    if (res.ok) emit({ who, avatar: parse(json) });
    else if (snapshot.who !== who) emit({ who, avatar: null });
  } catch {
    if (active.has(who) && snapshot.who !== who) emit({ who, avatar: null });
  } finally {
    if (inflight === who) inflight = null;
  }
}

/** Test seam: what the shared read currently holds. */
export const memberAvatarSnapshot = (): Snapshot => snapshot;

/** Test seam: forget the shared read. */
export function resetMemberAvatar() {
  snapshot = { who: null, avatar: null };
  inflight = null;
  active.clear();
  listeners.forEach((l) => l());
}

const subscribe = (l: () => void) => {
  listeners.add(l);
  return () => void listeners.delete(l);
};

/** Up to two initials from the person's own name, else the first letter of their email; "•" only when nothing is known. */
export function memberInitials(firstName: string | null | undefined, lastName: string | null | undefined, email: string | null | undefined): string {
  const a = (firstName ?? "").trim().charAt(0);
  const b = (lastName ?? "").trim().charAt(0);
  return (`${a}${b}` || (email ?? "").trim().charAt(0) || "•").toUpperCase();
}

/** The signed-in member: their own picture address (or null) and initials, or null when signed out. */
export function useMemberFace(): { src: string | null; initials: string } | null {
  const session = useCartStore((s) => s.session);
  const signedIn = isSignedIn(session);
  const who = signedIn ? `${session.cookie === true ? "sso" : "bearer"}:${session.email ?? ""}` : null;
  const snap = useSyncExternalStore(subscribe, () => snapshot, () => snapshot);

  useEffect(() => {
    if (!who) {
      if (snapshot.who !== null) emit({ who: null, avatar: null });
      return;
    }
    const release = trackMemberFace(who);
    const read = () => void refreshMemberAvatar(who);
    read();
    const onVisible = () => {
      if (document.visibilityState === "visible") read();
    };
    window.addEventListener("focus", read);
    document.addEventListener("visibilitychange", onVisible);
    const timer = window.setInterval(() => {
      if (snapshot.avatar?.src.startsWith("https://")) read();
    }, REFRESH_MS);
    return () => {
      window.removeEventListener("focus", read);
      document.removeEventListener("visibilitychange", onVisible);
      window.clearInterval(timer);
      release();
    };
  }, [who]);

  if (!who) return null;
  return { src: snap.who === who ? (snap.avatar?.src ?? null) : null, initials: memberInitials(session.firstName, session.lastName, session.email) };
}

/**
 * The chrome's face. Signed out: the guest `fallback` icon, unchanged. Signed in: that member's own picture, drawn with the shared round-avatar
 * treatment (a background scaled to 120% of the height and aligned to the TOP, as `.sc5-face--round`, so a face is never cut off); with no picture,
 * or one the browser cannot load, their initials.
 */
export function MemberAvatarImg({ fallback, alt = "", width, height, style, className }: { fallback: string; alt?: string; width: number; height: number; style?: CSSProperties; className?: string }) {
  const face = useMemberFace();
  const [failed, setFailed] = useState<string | null>(null);
  if (!face) {
    return (
      // eslint-disable-next-line @next/next/no-img-element
      <img alt={alt} width={width} height={height} className={className} data-member-avatar="guest" src={fallback} style={style} />
    );
  }
  const live = face.src && failed !== face.src ? face.src : null;
  return (
    <span
      role="img"
      aria-label={alt || undefined}
      className={className}
      data-member-avatar={live ? "picture" : "initials"}
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        boxSizing: "border-box",
        flex: "0 0 auto",
        width,
        height,
        borderRadius: "50%",
        overflow: "hidden",
        background: live ? "#fff" : "#0f2f4c",
        color: "#fff",
        fontSize: Math.round(height * 0.4),
        fontWeight: 600,
        lineHeight: 1,
        ...(live ? { backgroundImage: `url(${JSON.stringify(live)})`, backgroundRepeat: "no-repeat", backgroundPosition: "50% 0%", backgroundSize: "auto 120%" } : null),
      }}
    >
      {live ? (
        // a hidden probe loads the same address once; if it cannot be loaded the initials are drawn instead of a blank circle
        // eslint-disable-next-line @next/next/no-img-element
        <img key={live} src={live} alt="" hidden aria-hidden="true" decoding="async" referrerPolicy="no-referrer" onError={() => setFailed(live)} />
      ) : (
        <span aria-hidden="true">{face.initials}</span>
      )}
    </span>
  );
}
